/**
 * The console's client label: the `x-zion-client: console/<version>` header that `apiFetch` puts on
 * every CP API request (plan 2026-10-21-operator-tui, D10 and defaults row F3).
 *
 * The CP stores the label only as `details.client` in a read-audit row and never authorises on it.
 * The header name and the grammar live in `@zion/sdk-contracts`; this module is the console's own
 * value, as `sessionTransportHeaders` is the console's own copy of the transport header.
 *
 * The live data client (`LiveTopicRegistry`) opens a browser WebSocket, which cannot set a custom
 * header, so its upgrade request carries no label.
 */
import { ZION_CLIENT_HEADER, formatZionClient } from '@zion/sdk-contracts';

/**
 * The version part of the console's label: `'0'`.
 *
 * The console build exposes no package version to the bundle: the Vite config has no `define`
 * and no module reads the package manifest. So the label is `console/0` until the build gives one.
 */
export const CONSOLE_CLIENT_VERSION = '0';

/**
 * The whole label, `console/0`. `formatZionClient` runs once at module load and throws a
 * `RangeError` for a version outside the grammar, so a bad version fails the console at start and
 * never reaches the CP as a label the CP drops.
 */
export const CONSOLE_CLIENT_LABEL: string = formatZionClient('console', CONSOLE_CLIENT_VERSION);

/**
 * The label header, ready to spread into a `fetch` header map.
 *
 * Caller: `apiFetch`, on every request and on its one replay after a refresh.
 *
 * @returns a fresh object per call, always exactly one entry: `{ 'x-zion-client': 'console/0' }`.
 *   A fresh object, so a caller that mutates its own header map cannot reach a shared one.
 * @throws nothing. It reads no storage, no DOM and no network.
 */
export function consoleClientHeaders(): Record<string, string> {
  return { [ZION_CLIENT_HEADER]: CONSOLE_CLIENT_LABEL };
}
